/**
 * i18n minimaliste : map FR/EN + hook `useT()`.
 *
 * Pas de lib externe pour V1 (économie ~15 KB bundle). On ajoutera i18next si
 * besoin de plurals / interpolation complexe / RTL.
 *
 * Persiste la langue choisie en localStorage. Default = FR pour rester cohérent
 * avec le premier client (Gaz Systèmes). La langue est aussi influencée par
 * le `domain.langue` quand il est chargé.
 */

import { useEffect, useState } from 'react';

export type Locale = 'fr' | 'en';

const STORAGE_KEY = 'tc:locale';

type TranslationKey =
  | 'nav.dashboard'
  | 'nav.upload'
  | 'nav.catalogue'
  | 'nav.rules'
  | 'nav.platform'
  | 'nav.users'
  | 'nav.metrics'
  | 'dashboard.title'
  | 'dashboard.kpi.this_month'
  | 'dashboard.kpi.precision'
  | 'dashboard.kpi.total_ht'
  | 'dashboard.kpi.to_review'
  | 'dashboard.new_tender'
  | 'dashboard.filter.all'
  | 'dashboard.filter.ready'
  | 'dashboard.filter.reviewed'
  | 'dashboard.filter.exported'
  | 'dashboard.filter.processing'
  | 'dashboard.filter.failed'
  | 'dashboard.search.placeholder'
  | 'dashboard.search.from'
  | 'dashboard.search.to'
  | 'dashboard.trash'
  | 'dashboard.empty'
  | 'dashboard.col.name'
  | 'dashboard.col.client'
  | 'dashboard.col.status'
  | 'dashboard.col.lines'
  | 'dashboard.col.total'
  | 'dashboard.col.confidence'
  | 'dashboard.col.received'
  | 'upload.title'
  | 'upload.name'
  | 'upload.client'
  | 'upload.pdf'
  | 'upload.dropzone'
  | 'upload.submit'
  | 'upload.submitting'
  | 'upload.hint'
  | 'common.loading'
  | 'common.error'
  | 'common.cancel'
  | 'common.save'
  | 'common.delete'
  | 'common.restore'
  | 'common.back';

const DICT: Record<Locale, Record<TranslationKey, string>> = {
  fr: {
    'nav.dashboard': 'AO',
    'nav.upload': "Nouvel AO",
    'nav.catalogue': 'Catalogue',
    'nav.rules': 'Règles',
    'nav.platform': 'Plateforme',
    'nav.users': 'Users',
    'nav.metrics': 'Métriques',
    'dashboard.title': "Mes appels d'offres",
    'dashboard.kpi.this_month': 'AO ce mois',
    'dashboard.kpi.precision': 'Précision moyenne',
    'dashboard.kpi.total_ht': 'Total HT cumulé',
    'dashboard.kpi.to_review': 'À revoir',
    'dashboard.new_tender': '+ Nouveau AO',
    'dashboard.filter.all': 'Tous',
    'dashboard.filter.ready': 'À revoir',
    'dashboard.filter.reviewed': 'Revus',
    'dashboard.filter.exported': 'Exportés',
    'dashboard.filter.processing': 'En cours',
    'dashboard.filter.failed': 'En erreur',
    'dashboard.search.placeholder': 'ex: Hôpital, Mairie…',
    'dashboard.search.from': 'Du',
    'dashboard.search.to': 'Au',
    'dashboard.trash': '🗑 Corbeille',
    'dashboard.empty': "Aucun AO traité pour le moment.",
    'dashboard.col.name': 'Nom',
    'dashboard.col.client': 'Client',
    'dashboard.col.status': 'Statut',
    'dashboard.col.lines': 'Lignes',
    'dashboard.col.total': 'Total HT',
    'dashboard.col.confidence': 'Confiance H/M/B',
    'dashboard.col.received': 'Reçu',
    'upload.title': "Nouveau appel d'offres",
    'upload.name': "Nom de l'AO",
    'upload.client': 'Client final',
    'upload.pdf': "Fichier PDF de l'AO",
    'upload.dropzone': "Drag & drop le PDF ici, ou clique pour parcourir",
    'upload.submit': 'Lancer le traitement',
    'upload.submitting': 'Upload en cours…',
    'upload.hint': 'Le pipeline traite le PDF en 2-5 min selon la taille.',
    'common.loading': 'Chargement…',
    'common.error': 'Erreur',
    'common.cancel': 'Annuler',
    'common.save': 'Enregistrer',
    'common.delete': 'Supprimer',
    'common.restore': 'Restaurer',
    'common.back': '← Retour',
  },
  en: {
    'nav.dashboard': 'Tenders',
    'nav.upload': 'New tender',
    'nav.catalogue': 'Catalogue',
    'nav.rules': 'Rules',
    'nav.platform': 'Platform',
    'nav.users': 'Users',
    'nav.metrics': 'Metrics',
    'dashboard.title': 'My tenders',
    'dashboard.kpi.this_month': 'Tenders this month',
    'dashboard.kpi.precision': 'Avg. precision',
    'dashboard.kpi.total_ht': 'Total ex. VAT',
    'dashboard.kpi.to_review': 'To review',
    'dashboard.new_tender': '+ New tender',
    'dashboard.filter.all': 'All',
    'dashboard.filter.ready': 'To review',
    'dashboard.filter.reviewed': 'Reviewed',
    'dashboard.filter.exported': 'Exported',
    'dashboard.filter.processing': 'Processing',
    'dashboard.filter.failed': 'Failed',
    'dashboard.search.placeholder': 'e.g. Hospital, City Hall…',
    'dashboard.search.from': 'From',
    'dashboard.search.to': 'To',
    'dashboard.trash': '🗑 Trash',
    'dashboard.empty': 'No tenders yet.',
    'dashboard.col.name': 'Name',
    'dashboard.col.client': 'Client',
    'dashboard.col.status': 'Status',
    'dashboard.col.lines': 'Lines',
    'dashboard.col.total': 'Total ex. VAT',
    'dashboard.col.confidence': 'Conf. H/M/L',
    'dashboard.col.received': 'Received',
    'upload.title': 'New tender',
    'upload.name': 'Tender name',
    'upload.client': 'End client',
    'upload.pdf': 'Tender PDF file',
    'upload.dropzone': 'Drag & drop the PDF here, or click to browse',
    'upload.submit': 'Start processing',
    'upload.submitting': 'Uploading…',
    'upload.hint': 'The pipeline processes the PDF in 2-5 min depending on size.',
    'common.loading': 'Loading…',
    'common.error': 'Error',
    'common.cancel': 'Cancel',
    'common.save': 'Save',
    'common.delete': 'Delete',
    'common.restore': 'Restore',
    'common.back': '← Back',
  },
};

function resolveInitial(): Locale {
  if (typeof window === 'undefined') return 'fr';
  try {
    const stored = localStorage.getItem(STORAGE_KEY) as Locale | null;
    if (stored === 'fr' || stored === 'en') return stored;
  } catch {
    // ignore
  }
  if (typeof navigator !== 'undefined') {
    return navigator.language.startsWith('en') ? 'en' : 'fr';
  }
  return 'fr';
}

let currentLocale: Locale = resolveInitial();
const listeners = new Set<(l: Locale) => void>();

export function getLocale(): Locale {
  return currentLocale;
}

export function setLocale(l: Locale): void {
  currentLocale = l;
  try {
    localStorage.setItem(STORAGE_KEY, l);
  } catch {
    // ignore
  }
  listeners.forEach((fn) => fn(l));
}

export function t(key: TranslationKey, locale: Locale = currentLocale): string {
  return DICT[locale][key] ?? DICT.fr[key] ?? key;
}

export function useT(): {
  t: (key: TranslationKey) => string;
  locale: Locale;
  setLocale: (l: Locale) => void;
} {
  const [loc, setLoc] = useState<Locale>(currentLocale);
  useEffect(() => {
    const fn = (l: Locale) => setLoc(l);
    listeners.add(fn);
    return () => {
      listeners.delete(fn);
    };
  }, []);
  return {
    t: (key: TranslationKey) => t(key, loc),
    locale: loc,
    setLocale,
  };
}
